import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Chip,
  Paper,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip
} from '@mui/material';
import { FingerprintOutlined } from '@mui/icons-material';
import { User } from '../../../types';
import { BiometricsStatus, fetchBiometricsStatus, resetUserBiometrics } from '../../../allUsersApi';
import { ZERDE_PRIMARY } from '../types';
import { notify } from '../../../../../notifications';
import { apiErrorMessage } from '../../../../reports/edit/editApi';

interface UserBiometricsSectionProps {
  user: User;
}

export const UserBiometricsSection: React.FC<UserBiometricsSectionProps> = ({ user }) => {
  // users.reset_biometrics с учетом зоны и ранга считает сервер
  const canReset = user.canResetBiometrics === true;
  const [bioStatus, setBioStatus] = useState<BiometricsStatus | null>(null);
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [resetting, setResetting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoadingStatus(true);
    fetchBiometricsStatus(user.username)
      .then(status => {
        if (!cancelled) setBioStatus(status);
      })
      .catch(err => {
        if (cancelled) return;
        setBioStatus(null);
        notify.error(apiErrorMessage(err, 'Не удалось загрузить статус биометрии'));
      })
      .finally(() => {
        if (!cancelled) setLoadingStatus(false);
      });
    return () => {
      cancelled = true;
    };
  }, [user.username]);

  const handleReset = async () => {
    setResetting(true);
    try {
      notify.success(await resetUserBiometrics(user.id));
      setBioStatus({ registered: false, descriptorCount: 0 });
    } catch (err) {
      notify.error(apiErrorMessage(err, 'Не удалось сбросить биометрию'));
    } finally {
      setResetting(false);
      setConfirmOpen(false);
    }
  };

  return (
    <Box id="user-section-biometrics" sx={{ scrollMarginTop: '60px' }}>
      <Paper elevation={0} sx={{ p: 3.5, bgcolor: '#ffffff', borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: '0 2px 10px rgba(0,0,0,0.03)' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          <FingerprintOutlined sx={{ color: ZERDE_PRIMARY, fontSize: 22 }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#0f172a' }}>
            Биометрическая идентификация
          </Typography>
        </Box>
        <Typography variant="body2" sx={{ color: '#64748b', mb: 3 }}>
          Статус привязки Face ID сотрудника к системе
        </Typography>

        {loadingStatus ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
            <CircularProgress size={18} />
            <Typography variant="body2" color="text.secondary">Загрузка статуса...</Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: canReset ? 3 : 0 }}>
            <Paper elevation={0} sx={{ p: 2.2, bgcolor: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
              <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600 }}>СТАТУС РЕГИСТРАЦИИ</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                <Chip
                  size="small"
                  label={!bioStatus ? 'Нет данных' : bioStatus.registered ? 'Зарегистрирован' : 'Не настроен'}
                  color={bioStatus?.registered ? 'success' : 'default'}
                  sx={{ fontWeight: 600, borderRadius: '6px' }}
                />
              </Box>
            </Paper>

            <Paper elevation={0} sx={{ p: 2.2, bgcolor: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
              <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600 }}>СЛЕПКОВ В БАЗЕ</Typography>
              <Typography variant="body1" sx={{ fontWeight: 700, color: '#0f172a', mt: 0.5 }}>
                {bioStatus ? bioStatus.descriptorCount : '-'}
              </Typography>
            </Paper>
          </Box>
        )}

        {canReset && (
          <>
            {bioStatus?.registered && (
              <Alert
                severity="warning"
                sx={{ mb: 2.5, borderRadius: '10px', fontSize: '0.82rem' }}
              >
                Сброс биометрии вынудит сотрудника пройти повторную регистрацию Face ID в профиле.
              </Alert>
            )}

            <Tooltip title={!loadingStatus && !bioStatus?.registered ? 'Биометрические данные не привязаны' : ''}>
              <span>
                <Button
                  variant="outlined"
                  color="error"
                  disabled={!bioStatus?.registered || resetting || loadingStatus}
                  onClick={() => setConfirmOpen(true)}
                  startIcon={resetting ? <CircularProgress size={16} color="inherit" /> : <FingerprintOutlined />}
                  sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 600 }}
                >
                  {resetting ? 'Сброс...' : 'Сбросить биометрию'}
                </Button>
              </span>
            </Tooltip>
          </>
        )}
      </Paper>

      {/* Подтверждение сброса */}
      {canReset && (
        <Dialog
          open={confirmOpen}
          onClose={() => setConfirmOpen(false)}
          PaperProps={{ sx: { borderRadius: '12px', p: 1 } }}
        >
          <DialogTitle sx={{ fontWeight: 700, color: '#0f172a' }}>Сброс биометрии</DialogTitle>
          <DialogContent>
            <Typography variant="body1">
              Вы действительно хотите удалить биометрические данные сотрудника <strong>{user.username}</strong>?
            </Typography>
            <Typography variant="body2" color="error" sx={{ mt: 1, fontWeight: 500 }}>
              Сотруднику потребуется повторно зарегистрировать Face ID в профиле.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setConfirmOpen(false)} sx={{ textTransform: 'none', fontWeight: 600 }}>
              Отмена
            </Button>
            <Button
              variant="contained"
              color="error"
              disabled={resetting}
              onClick={handleReset}
              startIcon={resetting ? <CircularProgress size={16} /> : undefined}
              sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '6px' }}
            >
              {resetting ? 'Удаление...' : 'Да, сбросить'}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
};

export default UserBiometricsSection;
